import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '../api/client.js';

const KIND_ICON = { customer: '👥', note: '✏️', followup: '⏱' };
const KIND_LABEL = { customer: 'Customer', note: 'Note', followup: 'Follow-up' };

export default function GlobalSearch() {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Cmd/Ctrl-K to focus
  useEffect(() => {
    function onKey(e) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
      if (e.key === 'Escape') {
        setOpen(false);
        inputRef.current?.blur();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const r = useQuery({
    queryKey: ['search', q],
    queryFn: () => api.get('/search', { params: { q } }).then(res => res.data.data),
    enabled: q.trim().length >= 2,
    staleTime: 5_000,
  });

  function go(item) {
    setOpen(false); setQ(''); inputRef.current?.blur();
    nav(item.url);
  }

  function onKeyDown(e) {
    const items = r.data || [];
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => Math.min(items.length - 1, h + 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setHighlight(h => Math.max(0, h - 1)); }
    if (e.key === 'Enter' && items[highlight]) { e.preventDefault(); go(items[highlight]); }
  }

  return (
    <div className="relative w-full max-w-md">
      <input
        ref={inputRef}
        value={q}
        onChange={e => { setQ(e.target.value); setOpen(true); setHighlight(0); }}
        onFocus={() => q && setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={onKeyDown}
        placeholder="Search… (⌘K)"
        className="input w-full text-sm"
      />
      {open && q.trim().length >= 2 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded shadow-panel border border-panelDark z-50 max-h-[60vh] overflow-y-auto">
          {r.isLoading && <div className="p-3 text-muted text-sm">Searching…</div>}
          {r.data?.length === 0 && <div className="p-3 text-muted text-sm">No matches.</div>}
          {r.data?.map((item, i) => (
            <button
              key={`${item.kind}-${item.id}`}
              onMouseDown={() => go(item)}
              onMouseEnter={() => setHighlight(i)}
              className={`w-full text-left px-3 py-3 md:py-2 min-h-[44px] md:min-h-0 flex gap-2 items-start ${i === highlight ? 'bg-panel' : ''}`}
            >
              <span className="text-lg">{KIND_ICON[item.kind] || '•'}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm font-medium truncate">{item.title}</span>
                <span className="block text-xs text-muted truncate">{item.subtitle}</span>
              </span>
              <span className="text-[10px] uppercase tracking-wider text-muted self-center">{KIND_LABEL[item.kind]}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
